<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Tutorial/More_Fun_with_Projections';
    let nextLink= '/Tutorial/Projections_for_Expressions';
    
    import Figure from '$lib/figures/Figure.svelte';
</script>

<PrevNextSection {prevLink} {nextLink} />



<SectionComponent tag="h1" 
  id="expressions-in-freon-1"
  bind:intersecting={visibleById["expressions-in-freon-1"]}
  onIntersectChange={(visible) => setVisible("expressions-in-freon-1", visible)}
>Expressions in Freon</SectionComponent>
<p>Many DSLs include expressions like <code>24 + 56</code>. Even though they may look simple, anyone who has tried to build a language knows expressions can be tricky. In Freon, they therefore have a special place.</p>
<p>In this lesson we introduce expressions by adding grading rules to each <code>Topic</code>. To do this, we need to update the metamodel.
Open the file <code>edu-topics.ast</code> and add one line to the <code>Page</code> concept.</p>
<pre class="language-freon"><code class="language-freon">// Education/lesson4-defs/edu-topics.ast#L10-L14

abstract concept Page &#123;
    name: identifier;
    questions: Question[];
    grading: GradeScore[]; /* concept from 'edu-scoring.ast' */
&#125;</code></pre>
<p>We’ll define the <code>GradeScore</code> concept in a new file called <code>edu-scoring.ast</code>.
Add the following lines:</p>
<pre class="language-freon"><code class="language-freon">// Education/lesson4-defs/edu-scoring.ast#L1-L6

language Education

concept GradeScore &#123;
    grade: Grade;
    expr: ScoreExpression;
&#125;</code></pre>
<p>Yes, the <code>expr</code> property uses <code>ScoreExpression</code>, which represents our expressions. To design it well, let’s first understand what users want to express.</p>
<SectionComponent tag="h2" 
  id="the-requirements-2"
  bind:intersecting={visibleById["the-requirements-2"]}
  onIntersectChange={(visible) => setVisible("the-requirements-2", visible)}
>The Requirements</SectionComponent>
<p>Grading depends on answers to the questions on a page. For instance, if all answers are correct, the score should be the top grade; if all are incorrect, the lowest grade applies. Talking to our hypothetical client (<img src="/icons/smile.png" alt="Smiley" width="20" height="20">), we learn that teachers want to assign a grade when specific questions are correct, even if others matter less. They might write:</p>
<pre class="language-txt"><code class="language-txt">GradeC: Answer to questionX is correct AND the Number of Correct Answers = 3

GradeD: Answer to questionY is correct OR Answer to questionZ is correct AND the Number of Correct Answers &gt; 2</code></pre>
<p>So, what do we need?</p>
<ol>
<li>A number literal for the <strong>3</strong> and <strong>2</strong>.</li>
<li>Something that represents <strong>Number of Correct Answers</strong>.</li>
<li>A reference to a question on the page (for <strong>questionX</strong>, <strong>questionY</strong>, etc.).</li>
<li>Binary boolean expressions (<strong>AND</strong>, <strong>OR</strong>).</li>
<li>An equals comparison for <code>=</code>.</li>
<li>A greater-than comparison for <code>&gt;</code> (and we’ll add the others for completeness).</li>
</ol>
<p>Here’s how we define the first three items:</p>
<pre class="language-freon"><code class="language-freon">// Education/lesson4-defs/edu-scoring.ast#L8-L28

///////////////////////////////////
/// Expressions
//////////////////////////////////
abstract expression ScoreExpression &#123;
&#125;

/* The value of a question reference is the answer given to the
given question */
expression QuestionReference base ScoreExpression &#123;
    reference question: Question;
&#125;

/* The value of NrOfCorrectAnswers is the total number of correct
answers on a page. */
expression NrOfCorrectAnswers base ScoreExpression &#123;
&#125;

/* The value of a NumberLiteralExpression is simply a number, like '24' */
expression NumberLiteralExpression base ScoreExpression &#123;
    value: number;
&#125;</code></pre>
<p>Instead of <code>concept</code>, we use <code>expression</code> to tell Freon that instances of these types are expressions.<br>
<code>ScoreExpression</code> is the common base so we can use it wherever an expression is needed. Otherwise, the definitions look similar to ordinary concepts.</p>
<SectionComponent tag="h2" 
  id="binary-expressions-3"
  bind:intersecting={visibleById["binary-expressions-3"]}
  onIntersectChange={(visible) => setVisible("binary-expressions-3", visible)}
>Binary Expressions</SectionComponent>
<p>Freon provides extra support for binary expressions. We only need to declare that a concept is a binary expression. Below we define boolean <strong>AND</strong> and <strong>OR</strong>:</p>
<pre class="language-freon"><code class="language-freon">// Education/lesson4-defs/edu-scoring.ast#L30-L44

///////////////////////////////////
/// Boolean AND and OR
//////////////////////////////////
abstract binary expression BinaryExpression base ScoreExpression &#123;
    left: ScoreExpression;
    right: ScoreExpression;
&#125;

binary expression AndExpression base BinaryExpression &#123;
    priority = 1;
&#125;

binary expression OrExpression base BinaryExpression &#123;
    priority = 1;
&#125;</code></pre>
<p>Because all expressions share <code>ScoreExpression</code> as a base, any <code>ScoreExpression</code> can appear on the left or right side of a <code>BinaryExpression</code>.</p>
<p><code>AndExpression</code> and <code>OrExpression</code> don’t need extra properties (that’s typical), but we do set a <code>priority</code>. Expression trees need priorities to parse and render correctly.<br>
In math, multiplication has higher priority than addition, so <code>8 * 7 + 1</code> is <code>(8 * 7) + 1</code>, not <code>8 * (7 + 1)</code>.<br>
In Freon, a higher number means higher priority.</p>
<SectionComponent tag="h2" 
  id="the-comparison-expressions-4"
  bind:intersecting={visibleById["the-comparison-expressions-4"]}
  onIntersectChange={(visible) => setVisible("the-comparison-expressions-4", visible)}
>The Comparison Expressions</SectionComponent>
<p>To satisfy <code>=</code> and <code>&gt;</code>, we’ll also add <code>&lt;</code>, <code>&gt;=</code>, and <code>&lt;=</code> for completeness:</p>
<pre class="language-freon"><code class="language-freon">// Education/lesson4-defs/edu-scoring.ast#L46-L70

///////////////////////////////////
/// Comparisons: &lt;=, &gt;=, &gt;, &lt;, ===
//////////////////////////////////
abstract binary expression ComparisonExpression base BinaryExpression &#123;
&#125;

binary expression LessOrEqualsExpression base ComparisonExpression &#123;
    priority = 2;
&#125;

binary expression GreaterOrEqualsExpression base ComparisonExpression &#123;
    priority = 2;
&#125;

binary expression LessThenExpression base ComparisonExpression &#123;
    priority = 2;
&#125;

binary expression GreaterThenExpression base ComparisonExpression &#123;
    priority = 2;
&#125;

binary expression EqualsExpression base ComparisonExpression &#123;
    priority = 2;
&#125;</code></pre>
<p>Now generate the editor again and open the <code>lesson4</code> model. We switch models because the metamodel gained new concepts, and the new model contains instances of them.</p>
<p><code>%@$#&*!!!</code>—after all that, nothing changed!<br>
Right: the <code>Page</code> projection (or its children) doesn’t mention the new <code>grading</code> property, so it isn’t shown. Let’s fix that by updating the <code>Page</code> projection in <code>page-footing.edit</code>:</p>
<pre class="language-freon"><code class="language-freon">// Education/lesson4-defs/page-footing.edit#L5-L11

Page &#123;[
    Questions:
        $&#123;self.questions vertical&#125;

    Score
        $&#123;self.grading vertical&#125;
]&#125;</code></pre>
<p>Generate again and you’ll see (part of) the grading information. It’s not pretty yet, but the expressions are there.<br>
In the next lesson, you’ll learn how to <strong>project expressions</strong> nicely.</p>
<Figure
imageName={'tutorial/Tutorial-lesson4-screenshot1.png'}
caption={'Editor after adding grading to the metamodel'}
figureNumber={1}
/>


<PrevNextSection {prevLink} {nextLink} />